import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { LAYOUT_DESKTOP } from "../Constants";
import {
    parseSceneTransitionSettings,
    SCROLL_EFFECT_CLEAR_PROPS,
    SCROLL_EFFECT_MODE_CONFIG,
    SCROLL_TRIGGER_CONFIG,
    SCROLLING_SCENE_EFFECTS,
} from "./sceneMotionConfig";

export default class ScrollingSceneEffects {
    constructor(spread) {
        gsap.registerPlugin(ScrollTrigger);

        this.spread = spread;
        this.effects = [];
        this.frozen = false;
    }

    init() {
        this.kill();

        if (!this.shouldInit()) {
            return false;
        }

        this.spread.scenes.forEach((scene) => {
            this.createSceneEffect(scene);
        });

        if (this.effects.length > 0) {
            ScrollTrigger.refresh();
        }

        return this.effects.length > 0;
    }

    shouldInit() {
        return this.spread.scrolling_scenes &&
            // this.spread.active_layout === LAYOUT_DESKTOP &&
            !this.spread.screenshotmode &&
            !this.spread.pdfmode &&
            !this.prefersReducedMotion();
    }

    prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    createSceneEffect(scene) {
        if (scene.sticky || scene.isPopup) {
            return;
        }

        const sceneElement = scene.getSceneElement(false);
        const settings = parseSceneTransitionSettings(sceneElement.dataset.scenetransition);
        if (settings.type === "none") {
            return;
        }
        const effect = this.getEffectPreset(settings);

        if (!effect) {
            return;
        }

        const target = this.getEffectTarget(sceneElement, effect);

        if (!target) {
            return;
        }

        const mode = this.getEffectMode(effect);
        const effects = this.createEffectAnimations(sceneElement, target, effect, mode);

        this.effects.push(...effects);
    }

    getEffectPreset(settings) {
        // Own-property lookup: a published type like "constructor" would otherwise resolve to an
        // Object.prototype member and be called as an effect factory.
        const createEffect = Object.prototype.hasOwnProperty.call(SCROLLING_SCENE_EFFECTS, settings.type)
            ? SCROLLING_SCENE_EFFECTS[settings.type]
            : null;

        const effect = createEffect ? createEffect(settings) : null;
        return effect ? { ...effect, type: settings.type } : null;
    }

    getEffectMode(effect) {
        const mode = effect.mode || "out";

        return ["in", "out", "both"].includes(mode) ? mode : "out";
    }

    getEffectTarget(sceneElement, effect) {
        const targetSelector = effect.targetSelector || ".maglrspread";

        return sceneElement.querySelector(targetSelector);
    }

    createEffectAnimations(sceneElement, target, effect, mode) {
        const modes = mode === "both" ? ["in", "out"] : [mode];

        return modes.map((effectMode) => this.createEffectAnimation(sceneElement, target, effect, effectMode));
    }

    createEffectAnimation(sceneElement, target, effect, mode) {
        const timeline = gsap.timeline({ paused: true, defaults: { ease: "none" } });
        const triggerConfig = SCROLL_EFFECT_MODE_CONFIG[mode];
        const { from, to } = this.getEffectStates(effect, mode);

        this.addEffect(timeline, target, from, to);

        const trigger = ScrollTrigger.create({
            scroller: this.spread.getAnimationScroller(),
            animation: timeline,
            trigger: sceneElement,
            ...SCROLL_TRIGGER_CONFIG,
            ...triggerConfig,
            id: `${sceneElement.id}-effect-${effect.type}-${mode}`,
            markers: effect.markers,
        });

        return {
            timeline,
            trigger,
            targets: [target],
        };
    }

    getEffectStates(effect, mode) {
        const duration = effect.duration || effect.effectState.duration || effect.neutralState.duration || 1;

        if (mode === "in") {
            return {
                from: this.getTweenState(effect.effectState),
                to: this.getTweenState(effect.neutralState, duration),
            };
        }

        return {
            from: this.getTweenState(effect.neutralState),
            to: this.getTweenState(effect.effectState, duration),
        };
    }

    getTweenState(state, duration) {
        const props = { ...state };
        delete props.duration;

        if (!duration) {
            return props;
        }

        return {
            ...props,
            duration,
        };
    }

    addEffect(timeline, target, from, to) {
        timeline.fromTo(target, from, to);
    }

    // An open popup scrolls the document to 0 while the background scenes are frozen with
    // position:fixed; the scrubbed triggers would follow that scroll jump and snap the visible
    // background to its scroll-0 state. disable(false) stops the triggers without reverting
    // their inline styles, so the background keeps its current visual state.
    freeze() {
        if (this.frozen) {
            return;
        }

        this.frozen = true;
        this.effects.forEach(({ trigger }) => trigger.disable(false));
    }

    // enable() refreshes each trigger against the current scroll position, so this must run
    // after the popup close has restored the original scroll position and layout flow.
    unfreeze() {
        if (!this.frozen) {
            return;
        }

        this.frozen = false;
        this.effects.forEach(({ trigger }) => trigger.enable());
    }

    refresh() {
        // while frozen the layout is position:fixed for the popup, so measurements would be
        // wrong; unfreeze() refreshes the triggers once the normal flow is restored
        if (this.frozen) {
            return this.effects.length > 0;
        }

        if (!this.shouldInit()) {
            this.kill();
            return false;
        }

        this.effects.forEach(({ trigger }) => trigger.refresh());
        return this.effects.length > 0;
    }

    kill() {
        this.effects.forEach(({ timeline, trigger, targets }) => {
            trigger.kill();
            timeline.kill();
            gsap.set(targets, { clearProps: SCROLL_EFFECT_CLEAR_PROPS });
        });

        this.effects = [];
        this.frozen = false;
    }
}
